// Phase 22+ / Phase 13 — Admin Contracts Management
// =====================================================
// Master Admin only. Lets the operator upload, replace, activate,
// archive and manually re-send the three core ecosystem agreements:
//   - Affiliate
//   - Partner
//   - Franchise

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FileText, UploadCloud, History, Send, Archive, CheckCircle2,
  XCircle, Loader2, AlertCircle, Mail,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

const TYPES = [
  { key: "affiliate", label: "Affiliate Contract" },
  { key: "partner",   label: "Partner Contract" },
  { key: "franchise", label: "Franchise Contract" },
];

export default function AdminContracts() {
  const [active, setActive] = useState({});      // {affiliate: contract, partner: contract, franchise: contract}
  const [versions, setVersions] = useState([]);  // all contracts including archived
  const [emailLog, setEmailLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const reload = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const [a, l, log] = await Promise.all([
        api.get("/admin/contracts/active"),
        api.get("/admin/contracts"),
        api.get("/admin/contracts/email-log", { params: { limit: 50 } }),
      ]);
      setActive(a.data || {});
      setVersions(l.data?.contracts || []);
      setEmailLog(log.data?.log || []);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6" data-testid="admin-contracts">
      <header>
        <div className="eyebrow">Master Admin Only</div>
        <h1 className="font-anton text-3xl uppercase">Contracts</h1>
        <p className="text-sm text-lifted-dim mt-1 max-w-2xl">
          Manage active Affiliate / Partner / Franchise agreements. Auto-email triggers fire on role grant and approval. Old versions are archived, never deleted.
        </p>
      </header>

      {err && <div className="text-rose-300 text-sm" data-testid="contracts-err">{err}</div>}
      {ok && <div className="text-lifted-green text-sm" data-testid="contracts-ok">{ok}</div>}

      {busy && versions.length === 0 ? (
        <div className="text-center text-lifted-dim py-10">
          <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading contracts…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {TYPES.map((t) => (
            <ContractCard
              key={t.key}
              type={t.key}
              label={t.label}
              active={active[t.key]}
              versions={versions.filter((v) => v.contract_type === t.key)}
              onChange={(msg) => { setOk(msg); reload(); }}
              onError={setErr}
            />
          ))}
        </div>
      )}

      {/* Email log */}
      <section className="glass-panel p-4" data-testid="contract-email-log">
        <h2 className="font-anton text-xl uppercase mb-3 flex items-center gap-2">
          <Mail className="h-4 w-4 text-lifted-green" /> Email Delivery Log
        </h2>
        {emailLog.length === 0 ? (
          <p className="text-xs text-lifted-dim">No deliveries yet.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-[0.6rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
              <tr className="border-b border-white/5">
                <th className="text-left px-2 py-2">Sent</th>
                <th className="text-left px-2 py-2">User</th>
                <th className="text-left px-2 py-2">Type</th>
                <th className="text-left px-2 py-2">Trigger</th>
                <th className="text-left px-2 py-2">Status</th>
                <th className="text-left px-2 py-2">Error</th>
              </tr>
            </thead>
            <tbody>
              {emailLog.map((l) => (
                <tr key={l.id} className="border-b border-white/5"
                    data-testid={`contract-email-log-row-${l.id}`}>
                  <td className="px-2 py-1.5 text-lifted-dim">{(l.sent_at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="px-2 py-1.5 font-mono text-[10px] truncate max-w-[160px]">{l.user_id}</td>
                  <td className="px-2 py-1.5">{l.contract_type || "—"}</td>
                  <td className="px-2 py-1.5">{l.trigger}</td>
                  <td className="px-2 py-1.5">
                    <StatusPill status={l.delivery_status} />
                  </td>
                  <td className="px-2 py-1.5 text-[10px] text-rose-300 max-w-[260px] truncate" title={l.error_message || ""}>
                    {l.error_message || ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function ContractCard({ type, label, active, versions, onChange, onError }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [versionLabel, setVersionLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [showHistory, setShowHistory] = useState(false);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result || "").split(",")[1]);
        r.onerror = () => rej(new Error("Read failed"));
        r.readAsDataURL(file);
      });
      await api.post("/admin/contracts/upload", {
        contract_type: type,
        filename: file.name,
        content_type: file.type || "application/pdf",
        base64: b64,
        version_label: versionLabel.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      setVersionLabel(""); setNotes("");
      if (fileRef.current) fileRef.current.value = "";
      onChange(`Uploaded new ${label}`);
    } catch (e) { onError(apiErrorMessage(e)); }
    finally { setUploading(false); }
  };

  const activateVersion = async (id) => {
    try {
      await api.post(`/admin/contracts/${id}/activate`);
      onChange("Activated version.");
    } catch (e) { onError(apiErrorMessage(e)); }
  };

  return (
    <div className="glass-panel p-4 space-y-3" data-testid={`contract-card-${type}`}>
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 text-lifted-green" />
        <h3 className="font-anton uppercase text-base">{label}</h3>
      </div>
      {active ? (
        <div className="space-y-2 text-xs">
          <div className="flex items-center gap-2">
            <span className="px-1.5 py-0.5 rounded bg-lifted-green/20 text-lifted-green border border-lifted-green/40 text-[0.55rem] font-bebas tracking-[0.18em] uppercase">Active</span>
            <span className="font-bebas tracking-[0.18em] uppercase">v{active.version_label}</span>
          </div>
          <a href={active.file_url} target="_blank" rel="noreferrer"
             className="block text-lifted-green hover:underline truncate"
             data-testid={`contract-${type}-view`}>
            View {active.filename}
          </a>
          <div className="text-[10px] text-lifted-dim">
            Uploaded {(active.uploaded_at || "").slice(0, 10)} · {(active.size_bytes / 1024).toFixed(1)} KB
          </div>
          {active.notes && <div className="text-[10px] text-lifted-dim italic">{active.notes}</div>}
        </div>
      ) : (
        <div className="text-xs text-amber-300 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" /> No active {label.toLowerCase()} yet.
        </div>
      )}

      <div className="border-t border-white/5 pt-3 space-y-2">
        <input
          type="text"
          value={versionLabel}
          onChange={(e) => setVersionLabel(e.target.value)}
          placeholder="Version label (e.g. v2.1)"
          className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs"
          data-testid={`contract-${type}-version-input`}
        />
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes (optional)"
          className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs"
          data-testid={`contract-${type}-notes-input`}
        />
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,.pdf,.doc,.docx"
          onChange={(e) => handleUpload(e.target.files?.[0])}
          className="hidden"
          data-testid={`contract-${type}-file-input`}
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="btn-lifted text-xs w-full inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
          data-testid={`contract-${type}-upload`}
        >
          {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <UploadCloud className="h-3 w-3" />}
          {active ? "Replace contract" : "Upload contract"}
        </button>
      </div>

      {versions.length > 1 && (
        <button
          onClick={() => setShowHistory((v) => !v)}
          className="text-[10px] text-lifted-dim hover:text-lifted-green inline-flex items-center gap-1"
          data-testid={`contract-${type}-history-toggle`}
        >
          <History className="h-3 w-3" /> {showHistory ? "Hide" : "Show"} history ({versions.length})
        </button>
      )}
      {showHistory && (
        <ul className="text-[10px] space-y-1" data-testid={`contract-${type}-history`}>
          {versions.map((v) => (
            <li key={v.id} className="flex items-center justify-between gap-2 border-b border-white/5 py-1">
              <div className="truncate">
                v{v.version_label} ·{" "}
                <a href={v.file_url} target="_blank" rel="noreferrer" className="text-lifted-green hover:underline">{v.filename}</a>
                {v.active && <span className="ml-1 text-lifted-green">●</span>}
              </div>
              {!v.active && (
                <button onClick={() => activateVersion(v.id)}
                        className="text-[10px] text-lifted-dim hover:text-lifted-green"
                        data-testid={`contract-${type}-activate-${v.id}`}>
                  Activate
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    sent: { Icon: CheckCircle2, color: "text-lifted-green" },
    failed: { Icon: XCircle, color: "text-rose-300" },
    pending_credentials: { Icon: AlertCircle, color: "text-amber-300" },
  };
  const { Icon, color } = map[status] || { Icon: AlertCircle, color: "text-lifted-dim" };
  return (
    <span className={`inline-flex items-center gap-1 text-[0.6rem] ${color}`} title={status}>
      <Icon className="h-3 w-3" /> {status || "—"}
    </span>
  );
}
